'use client';

import { cn } from '@/lib/utils/cn';
import { Message } from '@/types';
import { formatRelativeTime } from '@/lib/utils/formatters';
import { User, Bot } from 'lucide-react';

interface ChatMessageProps {
  message: Message;
  isLatest?: boolean;
}

export function ChatMessage({ message, isLatest }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div
      className={cn(
        'flex gap-3 p-4',
        isUser ? 'flex-row-reverse' : 'flex-row',
        isLatest && 'animate-in fade-in slide-in-from-bottom-2'
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          isUser ? 'bg-indigo-600' : 'bg-gray-200'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-white" />
        ) : (
          <Bot className="h-4 w-4 text-gray-600" />
        )}
      </div>

      <div
        className={cn(
          'flex max-w-[80%] flex-col',
          isUser ? 'items-end' : 'items-start'
        )}
      >
        <div
          className={cn(
            'rounded-2xl px-4 py-2',
            isUser
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-900'
          )}
        >
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {message.content}
          </p>
        </div>

        <span className="mt-1 text-xs text-gray-400">
          {formatRelativeTime(message.created_at)}
          {message.metadata?.voice_used && (
            <span className="ml-2">🎤</span>
          )}
        </span>
      </div>
    </div>
  );
}
